<template>
  <div v-if="producto && !loadingFetch" class="yt-dashboard-theme pa-2 pa-sm-6">
    <div class="header-container mb-4 mb-sm-8 px-2 d-flex flex-wrap align-center" style="gap: 12px">
      <div class="d-flex flex-column header-titulo">
        <div class="d-flex align-center">
          <v-btn icon color="grey" class="mr-2 flex-shrink-0" @click="$router.back()">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h1
            class="font-weight-black white--text tracking-tighter"
            :class="$vuetify.breakpoint.xsOnly ? 'text-h6' : 'text-h4'"
          >
            {{ producto.descripcion_comercial || "Nuevo Producto" }}
          </h1>
          <v-chip
            label
            x-small
            class="ml-4 yt-status-chip hidden-sm-and-down"
            color="rgba(62, 166, 255, 0.1)"
            text-color="#3ea6ff"
          >
            DETALLES DEL PRODUCTO
          </v-chip>
        </div>
        <span class="caption grey--text ml-12 mt-n1 text-truncate">
          ID: #{{ producto.id }} • Ref: {{ producto.descripcion_global }}
        </span>
      </div>

      <v-spacer />

      <!-- En celular: barra fija abajo para tener Guardar siempre a mano -->
      <div class="action-bar pa-2 rounded-xl" :class="{ 'action-bar--movil': $vuetify.breakpoint.xsOnly }">
        <v-btn
          @click="deshacerCambios"
          :disabled="!hasChanges"
          text
          color="grey lighten-1"
          class="text-none font-weight-bold rounded-lg mr-2"
        >
          Descartar
        </v-btn>
        <v-btn
          class="px-8 font-weight-black text-none rounded-lg"
          :color="hasChanges ? '#3ea6ff' : '#272727'"
          depressed
          @click="validateForm"
          :disabled="!hasChanges"
          :loading="loading"
          :light="hasChanges"
          :dark="!hasChanges"
        >
          Guardar
        </v-btn>
      </div>
    </div>

    <v-row>
      <v-col cols="12" md="8">
        <v-card
          flat
          color="#161616"
          class="rounded-xl pa-4 pa-sm-8 border-yt mb-4 mb-sm-8 shadow-lg"
        >
          <div class="section-title mb-6">
            <v-icon small color="#3ea6ff" class="mr-2"
              >mdi-text-box-search-outline</v-icon
            >
            Información básica
          </div>

          <v-row>
            <v-col cols="12" md="5">
              <ProductoImagenes
                :producto="producto"
                class="rounded-xl shadow-2xl yt-main-image"
              />
            </v-col>
            <v-col cols="12" md="7">
              <div class="input-group">
                <span class="super-caption grey--text mb-2 d-block"
                  >NOMBRE COMERCIAL PARA EL PUNTO DE VENTA</span
                >
                <GenericAttributeInput
                  attributeName="descripcion_comercial"
                  label="Nombre"
                  :prefix="''"
                  :value="producto.descripcion_comercial"
                  filled
                  dark
                  color="#3ea6ff"
                  class="yt-input-large"
                  @update-attribute="handleAttributeUpdate"
                />
              </div>

              <v-alert
                dense
                text
                color="blue-grey lighten-4"
                class="mt-6 rounded-lg bg-dark-soft border-yt-thin"
              >
                <div class="d-flex align-center">
                  <v-icon x-small color="grey" class="mr-2">mdi-earth</v-icon>
                  <span class="super-caption grey--text"
                    >DESCRIPCIÓN GLOBAL DE REFERENCIA:</span
                  >
                </div>
                <div class="body-2 white--text font-italic mt-1 opacity-70">
                  "{{ producto.descripcion_global }}"
                </div>
              </v-alert>
            </v-col>
          </v-row>
        </v-card>

        <v-card
          v-if="presentacionUnica"
          flat
          color="#161616"
          class="rounded-xl pa-4 pa-sm-8 border-yt mb-4 mb-sm-8 shadow-lg"
        >
          <div class="section-title mb-6">
            <v-icon small color="#3ea6ff" class="mr-2">mdi-barcode-scan</v-icon>
            Identificación
          </div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <span class="super-caption grey--text mb-2 d-block">CÓDIGO DE BARRAS</span>
              <div class="d-flex align-center" style="gap: 8px">
                <v-text-field
                  v-model="identificacion.codigo_barras"
                  filled
                  dark
                  dense
                  hide-details
                  prepend-inner-icon="mdi-barcode-scan"
                />
                <v-btn
                  icon
                  small
                  :loading="loadingGenerarCodigo"
                  title="Generar código automáticamente"
                  @click="generarCodigoIdentificacion"
                >
                  <v-icon size="18" color="grey lighten-1">mdi-auto-fix</v-icon>
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <span class="super-caption grey--text mb-2 d-block">SKU</span>
              <v-text-field v-model="identificacion.sku" filled dark dense hide-details />
            </v-col>
          </v-row>
          <div class="d-flex justify-end mt-4">
            <v-btn
              depressed
              color="#3ea6ff"
              class="text-none font-weight-bold black--text"
              :loading="loadingIdentificacion"
              @click="guardarIdentificacion"
            >
              Guardar
            </v-btn>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <div class="sticky-sidebar">
          <v-card flat color="#161616" class="rounded-xl pa-4 pa-sm-6 border-yt mb-4 mb-sm-6">
            <div class="section-title mb-6">
              <v-icon small color="#3ea6ff" class="mr-2"
                >mdi-tune-variant</v-icon
              >
              Organización
            </div>

            <div class="form-spacing">
              <form-producto-es-elaboracion-propia
                :producto="producto"
                @set-elab-prop="
                  (val) =>
                    handleAttributeUpdate({
                      name: 'es_elaboracion_propia',
                      value: val,
                    })
                "
              />

              <v-divider class="my-4" dark style="opacity: 0.05" />

              <form-producto-proveedor
                :producto="producto"
                @set_proveedor="setProveedor"
              />

              <form-producto-categoria
                :producto="producto"
                :categoria="producto.categoria"
                @set_categoria="setCategoria"
              />

              <alicuota-iva-chips
                class="mt-4"
                :value="producto.alicuota_iva"
                @input="(val) => handleAttributeUpdate({ name: 'alicuota_iva', value: val })"
              />
            </div>
          </v-card>
        </div>
      </v-col>
    </v-row>

    <div class="px-2 mt-6 mb-2 d-flex justify-end">
      <v-btn
        text
        color="#ff5252"
        class="text-none font-weight-bold"
        @click="dialogDelete = true"
      >
        <v-icon left small>mdi-trash-can-outline</v-icon> Eliminar de la base de
        datos
      </v-btn>
    </div>

    <layout-dialog-delete
      v-if="dialogDelete"
      @close-dialog="dialogDelete = false"
      @processDelete="eliminarProducto(producto)"
    >
      <template #title>Eliminar producto</template>
      <template #message>
        ¿Estás seguro de que querés eliminar
        <strong>{{ producto.descripcion_comercial }}</strong
        >? Esta acción no se puede deshacer.
      </template>
    </layout-dialog-delete>
  </div>
</template>

<style scoped lang="scss">
.yt-dashboard-theme {
  background-color: #0f0f0f;
  min-height: 100vh;
  font-family: "Roboto", sans-serif;
}

/* Header & Action Bar */
.tracking-tighter {
  letter-spacing: -1.5px !important;
}
.action-bar {
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.header-titulo {
  min-width: 0;
  flex: 1 1 auto;
}
.header-titulo h1 {
  word-break: break-word;
  line-height: 1.2;
}
.action-bar--movil {
  position: fixed;
  left: 8px;
  right: 8px;
  bottom: 8px;
  z-index: 5;
  display: flex;
  justify-content: flex-end;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.6);
}

/* Cards & Borders */
.border-yt {
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.border-yt-thin {
  border: 1px solid rgba(255, 255, 255, 0.03) !important;
}
.border-danger {
  border: 1px solid rgba(255, 82, 82, 0.2) !important;
}
.bg-dark-soft {
  background-color: rgba(255, 255, 255, 0.02);
}

/* Typography */
.section-title {
  color: #fff;
  font-size: 0.75rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: flex;
  align-center: center;
}
.super-caption {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 1px;
}

/* Components */
.yt-status-chip {
  border: 1px solid rgba(62, 166, 255, 0.3) !important;
  font-weight: 900 !important;
  letter-spacing: 1px;
}

.image-wrapper {
  width: 100%;
  max-width: 180px;
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.02);
  }
}

.sticky-sidebar {
  position: sticky;
  top: 24px;
}
.form-spacing > * {
  margin-bottom: 16px;
}

/* Custom Input Style for the Dashboard */
.yt-input-large ::v-deep .v-input__slot {
  font-size: 1.25rem !important;
  font-weight: 700 !important;
  padding: 12px 16px !important;
}
</style>
<script>
import LayoutDialogDelete from "@/layouts/LayoutDialogDelete";
import AlicuotaIvaChips from "@/components/producto/form/AlicuotaIvaChips.vue";
import FormProductoProveedor from "@/components/producto/form/form.producto.proveedor.vue";
import FormProductoCategoria from "@/components/producto/form/form.producto.categoria.vue";
import FormProductoEsElaboracionPropia from "@/components/producto/form/FormProductoEsElaboracionPropia.vue";
import GenericAttributeInput from "@/components/util/GenericAttributeInput.vue";
import ProductoImagenes from "@/components/shared/productos/componentes/ProductoImagenes.vue";
import axios from "axios";
import { mapActions } from "vuex";
import ProductosAPI from "@/api/productos";

export default {
  name: "ProductoEmpresaUpdate",
  components: {
    AlicuotaIvaChips,
    GenericAttributeInput,
    FormProductoCategoria,
    FormProductoProveedor,
    FormProductoEsElaboracionPropia,
    LayoutDialogDelete,
    ProductoImagenes,
  },
  $_veeValidate: {
    validator: "new",
  },
  props: {
    productoProp: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingFetch: false,
      producto: null,
      originalProductoState: null,
      dialogDelete: false,
      // Independiente de `producto`/hasChanges: se guarda con su propio
      // botón contra el endpoint de la presentación, no con el de arriba
      // (que solo persiste campos de ProductoEmpresa).
      identificacion: { codigo_barras: "", sku: "" },
      loadingIdentificacion: false,
      loadingGenerarCodigo: false,
    };
  },
  computed: {
    // Con una sola presentación, código de barras y SKU se editan acá
    // (en "Presentaciones" solo queda la salida a agregar variantes).
    // Con 2+ presentaciones no hay una única a la que apuntar, así que
    // se gestionan del lado de "Presentaciones" como siempre.
    presentacionUnica() {
      const pps = this.producto && this.producto.presentaciones;
      return pps && pps.length === 1 ? pps[0] : null;
    },
    hasChanges() {
      if (!this.producto || !this.originalProductoState) return false;
      // Comparamos solo la data base, excluyendo presentaciones del chequeo si quieres
      // para que el botón "Guardar Todo" solo afecte a la Identidad y Clasificación.
      return (
        JSON.stringify(this.producto) !==
        JSON.stringify(this.originalProductoState)
      );
    },
  },

  methods: {
    ...mapActions("productos", ["actualizar_producto_empresa"]),

    eliminarProducto(producto) {
      ProductosAPI.delete(producto.id)
        .then(() => {
          this.dialogDelete = false;
          const savedQuery = JSON.parse(
            sessionStorage.getItem("productos_list_query") || "{}"
          );
          this.$router.push({
            name: "empresa.productosListado",
            query: savedQuery,
          });
          this.$updateBus.$emit("showSnackBar", {
            show: true,
            color: "green",
            text: "Producto eliminado",
            timeout: 2500,
          });
        })
        .catch(() => {
          this.dialogDelete = false;
          this.$updateBus.$emit("showSnackBar", {
            show: true,
            color: "red",
            text: "Error al eliminar",
            timeout: 2500,
          });
        });
    },

    deshacerCambios() {
      if (this.originalProductoState) {
        this.producto = JSON.parse(JSON.stringify(this.originalProductoState));
      }
    },

    handleAttributeUpdate({ name, value }) {
      this.$set(this.producto, name, value);
    },

    setCategoria(data) {
      this.$set(this.producto, "categoria", data);
    },

    setProveedor(proveedor) {
      this.$set(this.producto, "proveedor", proveedor);
    },

    validateForm() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.actualizarProducto(this.producto);
        }
      });
    },

    actualizarProducto(p) {
      this.loading = true;
      // Clonamos para no enviar datos innecesarios si el backend es estricto
      const payload = { ...p };

      this.actualizar_producto_empresa(payload)
        .then(() => {
          this.$updateBus.$emit("showSnackBar", {
            show: true,
            color: "success",
            text: "Cambios base guardados",
            timeout: 2000,
          });
          this.loading = false;
          this.$emit("refresh-producto");
        })
        .catch(() => {
          this.loading = false;
          this.$updateBus.$emit("showSnackBar", {
            show: true,
            color: "info",
            text: "Error al actualizar",
            timeout: 3000,
          });
        });
    },

    async guardarIdentificacion() {
      if (!this.presentacionUnica) return;

      this.loadingIdentificacion = true;
      try {
        const url = `/productos/productos/${this.producto.id}/presentaciones/${this.presentacionUnica.id}`;
        await axios.patch(url, this.identificacion);
        this.presentacionUnica.codigo_barras = this.identificacion.codigo_barras;
        this.presentacionUnica.sku = this.identificacion.sku;
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          color: "success",
          text: "Código y SKU actualizados",
          timeout: 2000,
        });
      } catch (error) {
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          color: "error",
          text: this.mensajeDeErrorIdentificacion(error),
          timeout: 4000,
        });
      } finally {
        this.loadingIdentificacion = false;
      }
    },

    async generarCodigoIdentificacion() {
      if (!this.presentacionUnica) return;

      this.loadingGenerarCodigo = true;
      try {
        const url = `/productos/productos/${this.producto.id}/presentaciones/${this.presentacionUnica.id}/generar-codigo`;
        const { data } = await axios.post(url);
        this.identificacion.codigo_barras = data.codigo_barras;
      } catch (error) {
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          color: "error",
          text: this.mensajeDeErrorIdentificacion(error),
          timeout: 4000,
        });
      } finally {
        this.loadingGenerarCodigo = false;
      }
    },

    // DRF puede devolver {"detail": "..."} o {"campo": ["mensaje"]} (ej.
    // codigo_barras/sku duplicados) — se muestra el primero que haya.
    mensajeDeErrorIdentificacion(error) {
      const data = error.response && error.response.data;
      if (!data) return "Ocurrió un error inesperado.";
      if (typeof data === "string") return data;
      if (data.detail) return data.detail;
      const primerCampo = Object.keys(data)[0];
      if (primerCampo) {
        const valor = data[primerCampo];
        return Array.isArray(valor) ? valor[0] : String(valor);
      }
      return "Ocurrió un error inesperado.";
    },
  },
  watch: {
    productoProp: {
      handler(newVal) {
        if (newVal) {
          this.producto = JSON.parse(JSON.stringify(newVal));
          this.originalProductoState = JSON.parse(JSON.stringify(newVal));
          this.loadingFetch = false;
        }
      },
      immediate: true,
    },
    presentacionUnica: {
      immediate: true,
      handler(pp) {
        this.identificacion = pp
          ? { codigo_barras: pp.codigo_barras || "", sku: pp.sku || "" }
          : { codigo_barras: "", sku: "" };
      },
    },
  },
};
</script>

